import styled from '@emotion/styled';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useDropzone, FileRejection } from 'react-dropzone';
import { BsCloudUpload, BsFolderFill } from 'react-icons/bs';

import { getAllowedFileTypes } from '../utilities/constants/mimeTypes';
import { checkExtensionSpoofing } from '../utilities/fileSignatureUtils';

import './BayFilesUpload.scss';

const replaceExtension = (filePath: string | undefined, newExt: string): string | undefined => {
	if (!filePath) return filePath;
	const lastDot = filePath.lastIndexOf('.');
	if (lastDot === -1) return filePath + newExt;
	return filePath.substring(0, lastDot) + newExt;
};

interface ContainerProps {
	isDragAccept: boolean;
	isDragReject: boolean;
	hasTableData: boolean;
	isPacketModal?: boolean;
	isLaunchpadConfig?: boolean;
}

const getColor = (props: ContainerProps): string => {
	if (props.isDragAccept) {
		return 'var(--bs-blue-500)';
	} else if (props.isDragReject || props.isLaunchpadConfig) {
		return 'var(--bs-red)';
	} else {
		return 'var(--bs-blue-300)';
	}
};

const getBorder = (props: ContainerProps): string => {
	if (!props.isDragAccept && props.hasTableData) {
		return 'hidden';
	} else {
		return 'dashed';
	}
};

const getBackgroundColor = (props: ContainerProps): string => {
	if (props.isDragAccept || !props.hasTableData) {
		return 'var(--bs-blue-50)';
	} else if (props.isDragReject) {
		return 'var(--bs-red-50)';
	}
	return 'transparent';
};

const getHeight = (props: ContainerProps): string => {
	if (props.isDragAccept && props.hasTableData && props.isPacketModal) {
		return '67vh';
	} else {
		return '100%';
	}
};

const Container = styled.div<ContainerProps>`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-width: 2px;
	border-radius: 8px;
	border-color: ${(props) => getColor(props)};
	border-style: ${(props) => getBorder(props)};
	background-color: ${(props) => getBackgroundColor(props)};
	outline: none;
	height: ${(props) => getHeight(props)};

	.drag-files-info {
		color: var(--bs-gray-900) !important;
	}

	.drag-files-types {
		color: var(--bs-gray-600) !important;
	}

	.bs-cloud-upload-icon {
		color: var(--bs-blue-300);
	}
`;

const TableDropZone = styled.div<ContainerProps>`
	flex: 1;
	flex-direction: column;
	display: flex;
	align-items: center;
	border-width: 2px;
	border-radius: 2px;
	border-color: ${(props) => getColor(props)};
	border-style: ${(props) => getBorder(props)};
	background-color: ${(props) => getBackgroundColor(props)};
	color: var(--bs-blue-drop-zone-bg);
	outline: none;

	.drag-files-info {
		color: var(--bs-gray-900) !important;
	}

	.drag-files-types {
		color: var(--bs-gray-600) !important;
		top: 134px;
	}
`;

const DropMessage = styled.div`
	position: fixed;
	bottom: 2%;
	display: flex;
	justify-content: center;
	margin: 15px;
	color: var(--bs-gray-900);
	z-index: 1000;

	.dropping-message-bubble {
		background-color: var(--bs-white);
		box-shadow: 0 4px 14px var(--bs-gray);
		border-radius: 8px;
		padding: 24px 32px 24px 32px;
		max-width: 350px;
		.dropping-section {
			margin-top: 5px;
			.section-name {
				font-weight: 600;
			}
		}
	}
`;

interface DropZoneData {
	isAllowRejectedFile?: boolean;
	maxSize?: number;
	allowedFiles?: Record<string, string[]>;
}

interface BayFilesUploadProps {
	t?: (key: string) => string;
	noData?: boolean;
	uploadingPopUpTitle?: string;
	onDrop: (files: File[], generateThumbnails?: boolean | FileRejection[]) => void;
	allowedFileTypes?: Record<string, string[]>;
	isMultiple?: boolean;
	allowedFileTypeslabel?: string;
	allowedFileTypesText?: string;
	hasTableData?: boolean;
	classNamesArray?: string[];
	useTableView?: boolean;
	isFilesGrid?: boolean;
	isPacketModal?: boolean;
	uploadLinkText?: string;
	shouldGenerateThumbnails?: boolean;
	isLaunchpadConfig?: boolean;
	isFileTypeSpoofed?: boolean;
	allowedFileUpload?: boolean;
	cloudIconSize?: number;
	dropZoneData?: DropZoneData;
	children?: React.ReactNode;
}

const BayFilesUpload: React.FC<BayFilesUploadProps> = ({
	t,
	noData,
	uploadingPopUpTitle = "User's Attachments",
	onDrop,
	allowedFileTypes = {},
	isMultiple = true,
	allowedFileTypeslabel = '',
	allowedFileTypesText = '',
	hasTableData,
	classNamesArray,
	useTableView,
	isFilesGrid,
	isPacketModal = false,
	uploadLinkText = 'Choose files',
	shouldGenerateThumbnails = false,
	isLaunchpadConfig = false,
	isFileTypeSpoofed = false,
	allowedFileUpload = true,
	cloudIconSize = 40,
	dropZoneData = {},
	children,
}) => {
	const allowedFileMimeTypes =
		Object.keys(allowedFileTypes).length > 0
			? getAllowedFileTypes(allowedFileTypes).mimeTypes
			: {};

	const { isAllowRejectedFile, maxSize, allowedFiles } = dropZoneData;
	const {
		acceptedFiles,
		fileRejections,
		getRootProps,
		getInputProps,
		open,
		isFocused,
		isDragAccept,
		isDragReject,
	} = useDropzone({
		noClick: true,
		noKeyboard: true,
		accept: allowedFileMimeTypes,
		multiple: isMultiple,
		...(allowedFiles && { accept: allowedFiles }),
  		...(typeof maxSize === 'number' && { maxSize }),
	});

	// Callers commonly pass an inline onDrop, so its identity changes on every parent
	// re-render. Reading these through a ref keeps them out of the effect below's
	// dependency array, so the effect only runs when acceptedFiles/fileRejections
	// actually change (a new selection) — not merely because the parent re-rendered.
	// Every read goes through the ref at the point of use (including after the await
	// in processFiles) rather than being destructured up front, so a re-render that
	// lands mid-await can't leave this effect holding a stale onDrop.
	const dropHandlerRef = useRef({
		onDrop,
		isFileTypeSpoofed,
		isAllowRejectedFile,
		shouldGenerateThumbnails,
	});

	useEffect(() => {
		dropHandlerRef.current = {
			onDrop,
			isFileTypeSpoofed,
			isAllowRejectedFile,
			shouldGenerateThumbnails,
		};
	}, [onDrop, isFileTypeSpoofed, isAllowRejectedFile, shouldGenerateThumbnails]);

	useEffect(() => {
		const processFiles = async () => {
			if (acceptedFiles.length > 0 || fileRejections.length > 0) {
				const allFiles = [...acceptedFiles, ...fileRejections.map(({ file }) => file)];

				const updatedFiles = await Promise.all(allFiles.map(async file => {
					const spoofingCheck = await checkExtensionSpoofing(file);
					if (spoofingCheck?.isSpoofed) {
						const newExt = spoofingCheck.expectedExtension;
						const newName = replaceExtension(file.name, newExt);
						const newFile = new File(
							[file],
							newName || file.name,
							{
								type: spoofingCheck.realMimeType,
								lastModified: file.lastModified
							}
						);
						// Type-safe property assignment
						Object.defineProperty(newFile, 'path', {
							value: replaceExtension((file as File & { path?: string }).path, newExt),
							writable: true,
						});
						Object.defineProperty(newFile, 'relativePath', {
							value: replaceExtension((file as File & { relativePath?: string }).relativePath, newExt),
							writable: true,
						});
						return newFile;
					}
					return file;
				}));

				dropHandlerRef.current.onDrop(updatedFiles, dropHandlerRef.current.shouldGenerateThumbnails);
			}
		};

		if (dropHandlerRef.current.isFileTypeSpoofed) {
			processFiles();
		} else {
			if (acceptedFiles.length > 0 || fileRejections.length > 0) {
				if (dropHandlerRef.current.isAllowRejectedFile) {
					dropHandlerRef.current.onDrop([...acceptedFiles], [...fileRejections]);
					return;
				}
				dropHandlerRef.current.onDrop([...acceptedFiles, ...fileRejections.map(({ file }) => file)], dropHandlerRef.current.shouldGenerateThumbnails);
			}
		}
	}, [acceptedFiles, fileRejections]);

	const cxStyles = classNamesArray
		? clsx([
				'bay-files-upload',
				'bay-files-upload-no-data w-100',
				...classNamesArray,
			])
		: 'bay-files-upload bay-files-upload-no-data w-100';

	const onPrimaryActionClick = () => {
		open();
	};

	return (
		<div
			className={
				!isFilesGrid
					? cxStyles
					: 'bay-files-upload-files-grid d-flex flex-column align-items-center w-100'
			}
		>
			{noData && !useTableView ? (
				isFilesGrid ? (
					<div
						{...getRootProps({
							isFocused,
							isDragAccept,
							isDragReject,
						})}
						className="d-flex flex-column align-items-center w-100"
					>
						<input {...getInputProps()} />
						{children}
						{isDragAccept && (
							<DropMessage>
								<div className="dropping-message-bubble">
									<div className="label text-center">
										Drag files to upload them to
									</div>
									<div className="dropping-section d-flex justify-content-center">
										<div className="me-2">
											<BsFolderFill color="var(--bs-blue-400)" size={16} />
										</div>
										<div className="section-name">{uploadingPopUpTitle}</div>
									</div>
								</div>
							</DropMessage>
						)}
					</div>
				) : (
					<Container
						{...getRootProps({
							isFocused,
							isDragAccept,
							isDragReject,
						})}
						hasTableData={false}
						isDragAccept={isDragAccept}
						isDragReject={isDragReject}
						isLaunchpadConfig={isLaunchpadConfig}
					>
						<input {...getInputProps()} />
						<div className="text-center m-5">
							<div>
								<BsCloudUpload className="bs-cloud-upload-icon" size={cloudIconSize} />
							</div>
							{allowedFileUpload && (
								<div className="drag-files-info d-flex align-items-center justify-center">
									<Button
										className="px-1 upload-files-link"
										variant="link"
										onClick={onPrimaryActionClick}
									>
										{uploadLinkText}
									</Button>{' '}
									<span className="d-none d-sm-block">
										or drag here to upload.
									</span>
								</div>
							)}
							{allowedFileTypeslabel && (
								<div className="allowed-file-types-label">
									{allowedFileTypeslabel}
								</div>
							)}
							{allowedFileTypesText && (
								<div className="allowed-file-types-text">
									{allowedFileTypesText}
								</div>
							)}
						</div>
					</Container>
				)
			) : useTableView ? (
				<TableDropZone
					{...getRootProps({
						isFocused,
						isDragAccept,
						isDragReject,
					})}
					hasTableData={true}
					isDragAccept={isDragAccept}
					isDragReject={isDragReject}
					isLaunchpadConfig={isLaunchpadConfig}
				>
					<input {...getInputProps()} />
					{children}
					{isDragAccept && (
						<DropMessage>
							<div className="dropping-message-bubble">
								<div className="label text-center">
									Drag files to upload them to
								</div>
								<div className="dropping-section d-flex justify-content-center">
									<div className="me-2">
										<BsFolderFill color="var(--bs-blue-400)" size={16} />
									</div>
									<div className="section-name">{uploadingPopUpTitle}</div>
								</div>
							</div>
						</DropMessage>
					)}
				</TableDropZone>
			) : (
				<Container
					{...getRootProps({
						isFocused,
						isDragAccept,
						isDragReject,
					})}
					className="d-flex flex-column align-items-center w-100"
					hasTableData={true}
					isDragAccept={isDragAccept}
					isDragReject={isDragReject}
					isLaunchpadConfig={isLaunchpadConfig}
					isPacketModal={isPacketModal}
				>
					<input {...getInputProps()} />
					{children}
					{isDragAccept && (
						<DropMessage>
							<div className="dropping-message-bubble">
								<div className="label text-center">
									Drag files to upload them{' '}
									{uploadingPopUpTitle ? 'to' : 'here'}
								</div>
								{uploadingPopUpTitle && (
									<div className="dropping-section d-flex justify-content-center">
										<div className="me-2">
											<BsFolderFill color="var(--bs-blue-400)" size={16} />
										</div>
										<div className="section-name">{uploadingPopUpTitle}</div>
									</div>
								)}
							</div>
						</DropMessage>
					)}
				</Container>
			)}
		</div>
	);
};

export default BayFilesUpload;
